import { AnimatePresence, motion } from 'framer-motion'
import { useState, type FormEvent } from 'react'
import { parseYouTubeId, type AttentionReason } from 'eyecontact'
import { Button } from './ui'

export function UrlBar({
  onLoad,
  disabled,
}: {
  onLoad: (id: string) => void
  disabled?: boolean
}) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const id = parseYouTubeId(value)
    if (!id) {
      setError("That doesn't look like a YouTube link or video id.")
      return
    }
    setError(null)
    onLoad(id)
  }

  return (
    <form onSubmit={submit} className="space-y-1.5">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          placeholder="Paste a YouTube URL or video id"
          aria-label="YouTube URL or video id"
          aria-invalid={error ? true : undefined}
          className="min-w-0 flex-1 rounded-md border border-line bg-surface-2 px-3 py-1.5 font-mono text-xs text-ink placeholder:text-faint focus:border-accent/60 focus:outline-none"
        />
        <Button type="submit" variant="ghost" disabled={disabled}>
          Load
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-2xs text-danger">
          {error}
        </p>
      )}
    </form>
  )
}

export function VideoStage({
  containerRef,
  paused,
  ready,
  error,
  cameraOn,
  recoveryProgress,
  reason,
  onStartCamera,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  paused: boolean
  ready: boolean
  error: string | null
  /** False until the gate is actually watching. Playback is blocked until then. */
  cameraOn: boolean
  /** 0..1 while paused, so the veil can show the resume filling up. */
  recoveryProgress: number
  reason: AttentionReason
  onStartCamera: () => void
}) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-line bg-[var(--stage-shell)]">
      <div ref={containerRef} className="absolute inset-0 [&>div]:h-full [&>div]:w-full [&_iframe]:h-full [&_iframe]:w-full" />

      {!ready && !error && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center bg-black">
          <div className="flex items-center gap-2 text-2xs text-faint">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
            Loading the YouTube player…
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 grid place-items-center bg-black p-6 text-center">
          <p className="max-w-sm text-xs leading-relaxed text-danger">{error}</p>
        </div>
      )}

      {/* Playback gate.
          The point of the app is that attention drives the video, so a video
          that plays with the camera off is just a video. The YouTube player
          lives in a cross-origin iframe whose controls we cannot intercept, so
          the only way to hold the click is to cover it. Unlike the pause veil
          below, this one deliberately DOES take pointer events. */}
      <AnimatePresence>
        {ready && !error && !cameraOn && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 z-10 grid place-items-center bg-black/80 backdrop-blur-[3px]"
          >
            <div className="flex max-w-xs flex-col items-center gap-3 px-6 text-center">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent" aria-hidden="true">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              <p className="text-sm font-medium text-white">Start the camera to play</p>
              <p className="text-2xs leading-relaxed text-white/55">
                eyecontact pauses the video when you look away, so it needs to see you before
                playback starts. Nothing leaves your machine.
              </p>
              <Button variant="primary" onClick={onStartCamera}>
                Start camera
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* The pause veil. Deliberately not clickable: it must never sit between
          the user and the player controls. */}
      <AnimatePresence>
        {paused && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none absolute inset-0 grid place-items-center bg-black/55 backdrop-blur-[2px]"
          >
            <motion.div
              initial={{ scale: 0.94, y: 6 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              className="flex flex-col items-center gap-2.5 rounded-lg border border-warn/30 bg-black/70 px-6 py-5"
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f5b544" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
                <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19M6.61 6.61A18.6 18.6 0 0 0 2 12s3 8 10 8a9 9 0 0 0 5.39-1.61" />
                <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24M2 2l20 20" />
              </svg>
              <p className="text-sm font-medium text-warn">
                {recoveryProgress > 0
                  ? 'Resuming…'
                  : reason === 'tab-hidden'
                    ? 'Paused — you left the tab'
                    : reason === 'window-blurred'
                      ? 'Paused — you switched apps'
                      : 'Paused — you looked away'}
              </p>
              <p className="text-2xs text-white/50">
                {reason === 'tab-hidden' || reason === 'window-blurred'
                  ? 'Come back to this window to resume'
                  : 'Look back at the screen to resume'}
              </p>

              {/* Progress towards resuming. Without it the veil looks frozen
                  while the gate is in fact already counting the viewer back in,
                  which reads as "it didn't notice me" and makes people give up
                  a few hundred milliseconds before it would have cleared. */}
              <div
                className="mt-0.5 h-0.5 w-32 overflow-hidden rounded-full bg-white/15"
                role="progressbar"
                aria-label="Resuming"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(recoveryProgress * 100)}
              >
                <motion.div
                  className="h-full rounded-full bg-accent"
                  animate={{ width: `${Math.round(recoveryProgress * 100)}%` }}
                  transition={{ duration: 0.12, ease: 'linear' }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
